import React, { useState, useEffect, useMemo } from 'react';
import { Sun, Moon } from 'lucide-react';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import apiClient from '../utils/apiClient';
import { useAuth } from '../context/UnifiedAuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import Icons from '../components/Icons';
import { useIsMobile } from '../hooks/useIsMobile';
import { useTheme } from '../hooks/useTheme';
import FantasySeasonCard from '../components/FantasySeasonCard';
import FantasyWeeklyCard from '../components/FantasyWeeklyCard';
import FantasyPlayerCard from '../components/FantasyPlayerCard';
import { Skeleton } from '../components/ui';

// Fantasy Scoring Configuration - Easy to modify
const FANTASY_SCORING = {
  quotaPerformance: {
    overQuota: 1,        // Points per stroke over quota
    underQuota: -1       // Points per stroke under quota
  },
  skins: 1.5,           // Points per skin
  ctps: 2,              // Points per CTP
  bonuses: {
    mostOverQuota: 2,   // Bonus for best quota performance (tied players get it too)
    leastToQuota: -2    // Penalty for worst quota performance (tied players get it too)
  }
};

const FantasyLeaderboard = () => {
  const { isAuthenticated } = useAuth();
  const [standings, setStandings] = useState([]);
  const [weeklyScores, setWeeklyScores] = useState([]);
  const [playerBreakdown, setPlayerBreakdown] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('season'); // 'season', 'weekly', or 'players'
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isAuthenticated) {
      fetchStandings();
      fetchEvents();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectedEvent) {
      fetchWeeklyScores(selectedEvent);
      fetchPlayerBreakdown(selectedEvent);
    }
  }, [selectedEvent]);

  // Initialize tooltips
  useEffect(() => {
    tippy('.tooltip-trigger', {
      placement: 'top',
      arrow: true,
      maxWidth: 200,
      delay: [500, 200],
    });
  }, [weeklyScores, standings, playerBreakdown, activeTab]);

  const fetchStandings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/fantasy/standings');
      setStandings(Array.isArray(response.data.standings) ? response.data.standings : []);
    } catch (error) {
      setStandings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await apiClient.get('/fantasy/events');
      setEvents(response.data);
    } catch (error) {
      setEvents([]);
    }
  };

  const fetchWeeklyScores = async (eventId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/fantasy/scores/${eventId}`);
      setWeeklyScores(Array.isArray(response.data.scores) ? response.data.scores : []);
    } catch (error) {
      setWeeklyScores([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerBreakdown = async (eventId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/fantasy/player-breakdown/${eventId}`);
      setPlayerBreakdown(Array.isArray(response.data.players) ? response.data.players : []);
    } catch (error) {
      setPlayerBreakdown([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate Player Stats (Best/Worst Quota)
  const playerStats = useMemo(() => {
    let bestPlayerName = null;
    let worstPlayerName = null;
    let bestPlayerQuotaDiff = null;
    let worstPlayerQuotaDiff = null;
    let minQuota = null;
    let maxQuota = null;

    // Helper to update stats
    const updateStats = (name, quotaDiff) => {
      if (bestPlayerQuotaDiff === null || quotaDiff > bestPlayerQuotaDiff) {
        bestPlayerQuotaDiff = quotaDiff;
        bestPlayerName = name;
      }
      if (worstPlayerQuotaDiff === null || quotaDiff < worstPlayerQuotaDiff) {
        worstPlayerQuotaDiff = quotaDiff;
        worstPlayerName = name;
      }
      if (minQuota === null || quotaDiff < minQuota) minQuota = quotaDiff;
      if (maxQuota === null || quotaDiff > maxQuota) maxQuota = quotaDiff;
    };

    if (playerBreakdown && playerBreakdown.length > 0) {
      playerBreakdown.forEach(player => {
        if (player.score != null && player.quota != null) {
          updateStats(player.player_name, player.score - player.quota);
        }
      });
    } else {
      // Fallback to picks if player breakdown not available
      const playerPerformance = {};
      weeklyScores.forEach(s => {
        if (s.picks) {
          Object.values(s.picks).forEach(pick => {
            if (pick.score !== null && pick.quota !== null && pick.name) {
              const quotaDiff = pick.score - pick.quota;
              if (!playerPerformance[pick.name] || quotaDiff > playerPerformance[pick.name]) {
                playerPerformance[pick.name] = quotaDiff;
              }
            }
          });
        }
      });
      Object.entries(playerPerformance).forEach(([name, diff]) => updateStats(name, diff));
    }

    return {
      bestPlayerName,
      worstPlayerName,
      bestPlayerQuotaDiff,
      worstPlayerQuotaDiff,
      minQuota,
      maxQuota
    };
  }, [weeklyScores, playerBreakdown]);

  // Calculate Corrected Scores
  const correctedScores = useMemo(() => {
    return (weeklyScores || []).map(score => {
      let correctTotalPoints = 0;
      let quotaPoints = 0;
      let hasBestQuotaBonus = false;
      let hasWorstQuotaPenalty = false;

      if (score.picks) {
        Object.values(score.picks).forEach(pick => {
          if (pick.score !== null && pick.quota !== null) {
            const quotaDiff = pick.score - pick.quota;
            correctTotalPoints += quotaDiff;
            quotaPoints += quotaDiff;

            if (pick.name === playerStats.bestPlayerName && playerStats.bestPlayerQuotaDiff > 0) {
              correctTotalPoints += FANTASY_SCORING.bonuses.mostOverQuota;
              hasBestQuotaBonus = true;
            }
            if (pick.name === playerStats.worstPlayerName) {
              correctTotalPoints += FANTASY_SCORING.bonuses.leastToQuota;
              hasWorstQuotaPenalty = true;
            }
          }
          correctTotalPoints += (pick.skins || 0) * FANTASY_SCORING.skins;
          correctTotalPoints += (pick.ctps || 0) * FANTASY_SCORING.ctps;
        });
      }
      return {
        ...score,
        correctTotalPoints,
        quotaPoints,
        hasBestQuotaBonus,
        hasWorstQuotaPenalty
      };
    }).sort((a, b) => b.correctTotalPoints - a.correctTotalPoints);
  }, [weeklyScores, playerStats]);

  // Process Player Breakdown
  const processedPlayerBreakdown = useMemo(() => {
    return (playerBreakdown || []).map(player => {
      const quotaPerf = player.score != null && player.quota != null ? player.score - player.quota : null;
      const isWorst = quotaPerf !== null && quotaPerf === playerStats.minQuota;
      const isBest = quotaPerf !== null && quotaPerf === playerStats.maxQuota && playerStats.maxQuota > 0;

      let correctFantasyPoints = 0;
      if (quotaPerf !== null) correctFantasyPoints += quotaPerf;
      correctFantasyPoints += (player.skins || 0) * FANTASY_SCORING.skins;
      correctFantasyPoints += (player.ctps || 0) * FANTASY_SCORING.ctps;

      if (isBest) correctFantasyPoints += FANTASY_SCORING.bonuses.mostOverQuota;
      if (isWorst) correctFantasyPoints += FANTASY_SCORING.bonuses.leastToQuota;

      return { ...player, isBest, isWorst, correctFantasyPoints, quotaPerf };
    });
  }, [playerBreakdown, playerStats]);

  const ScoreBreakdown = ({ score }) => {
    const quotaSign = score.quotaPoints > 0 ? '+' : '';
    return (
      <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
        <div>
          Quota: <span className={score.quotaPoints > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : score.quotaPoints < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
            {quotaSign}{score.quotaPoints}pts
          </span>
        </div>
        <div>Skins: {((score.skins_count || 0) * FANTASY_SCORING.skins).toFixed(1)}pts</div>
        <div>CTPs: {(score.ctp_count || 0) * FANTASY_SCORING.ctps}pts</div>
        {score.hasBestQuotaBonus && (
          <div>Bonus: <span className="text-green-600 dark:text-green-400 font-semibold">+{FANTASY_SCORING.bonuses.mostOverQuota}pts</span></div>
        )}
        {score.hasWorstQuotaPenalty && (
          <div>Penalty: <span className="text-red-600 dark:text-red-400 font-semibold">{FANTASY_SCORING.bonuses.leastToQuota}pts</span></div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                  Fantasy Leaderboard
                </h1>
                <p className="text-gray-600 dark:text-navy-400 mt-1">
                  Fireball Fantasy Fiasco Standings
                </p>
              </div>

              <div className="flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleTheme}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-navy-900
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDarkMode}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Tab Navigation */}
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700 mb-6 overflow-x-auto">
            <div className="border-b border-gray-200 dark:border-navy-700 min-w-max">
              <nav className="flex space-x-8 px-6">
                {['season', 'weekly', 'players'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === tab
                      ? 'border-forest-500 text-forest-600 dark:text-forest-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                      }`}
                  >
                    {tab === 'season' ? 'Season Standings' :
                      tab === 'weekly' ? 'Weekly Breakdown' : 'Player Performance'}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="bg-white dark:bg-navy-900 dark:border dark:border-gray-800 rounded-lg shadow p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-4 w-12" />
                  </div>
                  <Skeleton className="h-4 w-3/4" />
                  <div className="space-y-2">
                    {[1, 2, 3].map(row => (
                      <div key={row} className="flex items-center space-x-2">
                        <Skeleton className="w-8 h-8 rounded-full" />
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-10" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Authentication Required */}
          {!isAuthenticated && (
            <div className="bg-white dark:bg-navy-800 rounded-lg shadow-md p-8 sm:p-12 text-center">
              <div className="flex justify-center mb-4 text-gray-600 dark:text-gray-400">
                <Icons.Lock className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-4">Login Required</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Please log in to view fantasy golf leaderboards and scores</p>
              <button
                onClick={() => setShowAuthModal(true)}
                className="bg-forest-600 text-white px-6 py-3 rounded-lg hover:bg-forest-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-navy-800 transition-colors"
              >
                Login to Continue
              </button>
            </div>
          )}

          {/* Season Standings Tab */}
          {activeTab === 'season' && !loading && isAuthenticated && (
            <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700 p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-forest-900 dark:text-white">Season Standings</h2>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {(standings || []).length} participants
                </div>
              </div>

              {standings && standings.length > 0 ? (
                isMobile ? (
                  <div className="space-y-4">
                    {standings.map((participant, index) => (
                      <FantasySeasonCard
                        key={participant.participant_id}
                        participant={participant}
                        rank={index + 1}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl">
                    <table className="w-full border-collapse bg-white dark:bg-navy-800 overflow-hidden">
                      <thead className="bg-forest-900 dark:bg-navy-700 text-white">
                        <tr>
                          <th className="p-3 text-center font-semibold">Rank</th>
                          <th className="p-3 text-left font-semibold">Participant</th>
                          <th className="p-3 text-center font-semibold">Events Played</th>
                          <th className="p-3 text-center font-semibold">Total Points</th>
                          <th className="p-3 text-center font-semibold">Average/Event</th>
                          <th className="p-3 text-center font-semibold">Best Week</th>
                          <th className="p-3 text-center font-semibold">Winnings</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
                        {standings.map((participant, index) => (
                          <tr key={participant.participant_id} className={`${index % 2 === 0 ? "bg-gray-50 dark:bg-navy-800/50" : "bg-white dark:bg-navy-800"
                            } hover:bg-forest-50 dark:hover:bg-navy-700/50 transition-colors ${index < 3 ? "border-l-4 border-gold-400" : ""
                            }`}>
                            <td className="p-4 text-center font-semibold dark:text-gray-200">
                              <div className="flex items-center justify-center">
                                {index === 0 && <span className="text-yellow-500 mr-2"><Icons.Trophy /></span>}
                                {index === 1 && <span className="text-gray-400 mr-2"><Icons.Medal /></span>}
                                {index === 2 && <span className="text-orange-500 mr-2"><Icons.Medal /></span>}
                                <span className="text-lg font-bold">{index + 1}</span>
                              </div>
                            </td>
                            <td className="p-4 text-left dark:text-gray-200">
                              <span className="cursor-help tooltip-trigger" data-tippy-content={participant.participant_name || participant.participant_id}>
                                {participant.participant_name || participant.participant_id}
                              </span>
                            </td>
                            <td className="p-4 text-center dark:text-gray-300">{participant.events_played}</td>
                            <td className="p-4 text-center">
                              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{participant.total_points || 0}</span>
                            </td>
                            <td className="p-4 text-center dark:text-gray-300">
                              {participant.avg_points_per_event && !isNaN(participant.avg_points_per_event)
                                ? Number(participant.avg_points_per_event).toFixed(1)
                                : '0.0'}
                            </td>
                            <td className="p-4 text-center">
                              <span className="font-semibold text-green-600 dark:text-green-400">{participant.best_week || 0}</span>
                            </td>
                            <td className="p-4 text-center">
                              <span className={`text-lg font-bold ${participant.winnings > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                                ${participant.winnings || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No fantasy data available yet.</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Standings will appear after the first event with fantasy picks is completed.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Weekly Breakdown Tab */}
          {activeTab === 'weekly' && isAuthenticated && (
            <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700 p-4 sm:p-6">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-forest-900 dark:text-white">Weekly Breakdown</h2>
                </div>

                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Event</label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-200 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Choose an event...</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.course_name} - {new Date(event.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!loading && selectedEvent && correctedScores && correctedScores.length > 0 && (
                isMobile ? (
                  <div className="space-y-4">
                    {correctedScores.map((score, index) => (
                      <FantasyWeeklyCard
                        key={score.participant_id}
                        score={score}
                        rank={index + 1}
                        bestPlayerName={playerStats.bestPlayerName}
                        worstPlayerName={playerStats.worstPlayerName}
                        bestPlayerQuotaDiff={playerStats.bestPlayerQuotaDiff}
                        FANTASY_SCORING={FANTASY_SCORING}
                        breakdown={<ScoreBreakdown score={score} />}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl">
                    <table className="w-full border-collapse bg-white dark:bg-navy-800 overflow-hidden">
                      <thead className="bg-forest-900 dark:bg-navy-700 text-white">
                        <tr>
                          <th className="p-3 text-center font-semibold">Rank</th>
                          <th className="p-3 text-left font-semibold">Participant</th>
                          <th className="p-3 text-center font-semibold">Picks & Performance</th>
                          <th className="p-3 text-center font-semibold">Score Breakdown</th>
                          <th className="p-3 text-center font-semibold">Total Points</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
                        {correctedScores.map((score, index) => (
                          <tr key={score.participant_id} className={`${index % 2 === 0 ? "bg-gray-50 dark:bg-navy-800/50" : "bg-white dark:bg-navy-800"
                            } hover:bg-forest-50 dark:hover:bg-navy-700/50 transition-colors ${index < 3 ? "border-l-4 border-gold-400" : ""
                            }`}>
                            <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300 dark:border-gray-600">
                              <div className="flex items-center">
                                {index === 0 && <span className="text-yellow-500 mr-2"><Icons.Trophy /></span>}
                                {index === 1 && <span className="text-gray-400 mr-2"><Icons.Medal /></span>}
                                {index === 2 && <span className="text-orange-500 mr-2"><Icons.Medal /></span>}
                                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{index + 1}</span>
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap border-r border-gray-300 dark:border-gray-600">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help tooltip-trigger" data-tippy-content={score.participant_name || score.participant_id}>
                                {score.participant_name || score.participant_id}
                              </div>
                            </td>
                            <td className="px-3 py-4 border-r border-gray-300 dark:border-gray-600">
                              <div className="space-y-2">
                                {score.picks && Object.entries(score.picks).map(([tier, pick], i) => {
                                  const quotaDiff = pick.score && pick.quota ? pick.score - pick.quota : null;
                                  const quotaSign = quotaDiff > 0 ? '+' : '';
                                  const correctQuotaPoints = quotaDiff || 0;
                                  const correctSkinsPoints = (pick.skins || 0) * FANTASY_SCORING.skins;
                                  const correctCtpPoints = (pick.ctps || 0) * FANTASY_SCORING.ctps;
                                  const correctTotalPoints = correctQuotaPoints + correctSkinsPoints + correctCtpPoints;

                                  const isBestPick = pick.name === playerStats.bestPlayerName && playerStats.bestPlayerQuotaDiff > 0;
                                  const isWorstPick = pick.name === playerStats.worstPlayerName;

                                  let highlight = 'text-gray-900 dark:text-gray-100';
                                  if (isBestPick) highlight = 'bg-green-200 dark:bg-green-900/50 text-green-900 dark:text-green-200 font-bold border-2 border-green-500';
                                  else if (isWorstPick) highlight = 'bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-200 font-bold border-2 border-red-500';

                                  return (
                                    <div key={i} className="text-xs flex items-center gap-x-2 py-0">
                                      <span className={`w-8 text-center rounded-full text-[10px] font-bold flex-shrink-0 ${i === 0 ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' :
                                        i === 1 ? 'bg-gray-100 text-gray-700 border border-gray-300' :
                                          'bg-orange-100 text-orange-700 border border-orange-300'
                                        }`}>T{i + 1}</span>
                                      <span className={`font-medium px-1 py-0.5 rounded cursor-help tooltip-trigger flex-grow min-w-0 ${highlight}`} data-tippy-content={pick.name}>
                                        {pick.name}
                                      </span>
                                      <span className="text-center flex-shrink-0 w-8">
                                        {quotaDiff !== null && (
                                          <span className={quotaDiff >= 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-red-600 dark:text-red-400 font-semibold'}>
                                            {quotaSign}{quotaDiff}
                                          </span>
                                        )}
                                      </span>
                                      <span className="text-center flex-shrink-0 w-12">
                                        {pick.skins > 0 && (
                                          <div className="text-center">
                                            <span className="text-blue-600 dark:text-blue-400 font-medium flex items-center justify-center gap-1">
                                              <Icons.Target /> {pick.skins}
                                            </span>
                                          </div>
                                        )}
                                      </span>
                                      <span className="text-center flex-shrink-0 w-12">
                                        {pick.ctps > 0 && (
                                          <div className="text-center">
                                            <span className="text-purple-600 dark:text-purple-400 font-medium flex items-center justify-center gap-1">
                                              <Icons.Location /> {pick.ctps}
                                            </span>
                                          </div>
                                        )}
                                      </span>
                                      <span className={`font-bold text-right flex-shrink-0 w-12 ${correctTotalPoints >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                                        {correctTotalPoints}pts
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                            <td className="px-3 py-4 border-r border-gray-300 dark:border-gray-600">
                              <ScoreBreakdown score={score} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{score.correctTotalPoints}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {!loading && selectedEvent && (!weeklyScores || weeklyScores.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No scores available for this event.</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Scores will appear after the event is completed and fantasy points are calculated.
                  </p>
                </div>
              )}

              {!selectedEvent && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Select an event to view weekly scores and breakdowns.</p>
                </div>
              )}
            </div>
          )}

          {/* Player Performance Tab */}
          {activeTab === 'players' && isAuthenticated && (
            <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700 p-4 sm:p-6">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-forest-900 dark:text-white">Player Performance Breakdown</h2>
                </div>

                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Event</label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-200 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Choose an event...</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.course_name} - {new Date(event.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!loading && selectedEvent && processedPlayerBreakdown && processedPlayerBreakdown.length > 0 && (
                isMobile ? (
                  <div className="space-y-4">
                    {processedPlayerBreakdown.map((player, index) => (
                      <FantasyPlayerCard
                        key={`${player.player_name}-${player.tier}`}
                        player={player}
                        isBest={player.isBest}
                        isWorst={player.isWorst}
                        correctFantasyPoints={player.correctFantasyPoints}
                        FANTASY_SCORING={FANTASY_SCORING}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl">
                    <table className="w-full border-collapse bg-white dark:bg-navy-800 overflow-hidden">
                      <thead className="bg-forest-900 dark:bg-navy-700 text-white">
                        <tr>
                          <th className="p-3 text-left font-semibold">Player</th>
                          <th className="p-3 text-center font-semibold">Tier</th>
                          <th className="p-3 text-center font-semibold">Score/Quota</th>
                          <th className="p-3 text-center font-semibold">Skins</th>
                          <th className="p-3 text-center font-semibold">CTPs</th>
                          <th className="p-3 text-center font-semibold">Fantasy Points</th>
                          <th className="p-3 text-left font-semibold">Picked By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
                        {processedPlayerBreakdown.map((player, index) => (
                          <tr key={`${player.player_name}-${player.tier}`} className={`${index % 2 === 0 ? "bg-gray-50 dark:bg-navy-800/50" : "bg-white dark:bg-navy-800"
                            } hover:bg-forest-50 dark:hover:bg-navy-700/50 transition-colors ${player.correctFantasyPoints >= 10 ? 'border-l-4 border-green-400' :
                              player.correctFantasyPoints < 0 ? 'border-l-4 border-red-400' :
                                player.isBest ? 'border-l-4 border-gold-400' :
                                  player.isWorst ? 'border-l-4 border-gray-400' : ''
                            }`}>
                            <td className="p-4 text-left dark:text-gray-200">
                              <div className="font-medium">
                                {player.player_name}
                                {player.isBest && (
                                  <span className="ml-2 text-xs text-green-700 dark:text-green-400 font-bold flex items-center gap-1">
                                    <Icons.Trophy /> Best Quota
                                  </span>
                                )}
                                {player.isWorst && (
                                  <span className="ml-2 text-xs text-red-700 dark:text-red-400 font-bold flex items-center gap-1">
                                    <Icons.Frown /> Worst Quota
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${player.tier === 1 ? 'bg-yellow-100 text-yellow-800' :
                                player.tier === 2 ? 'bg-gray-100 text-gray-800' :
                                  'bg-orange-100 text-orange-800'
                                }`}>
                                Tier {player.tier}
                              </span>
                            </td>
                            <td className="p-4 text-center dark:text-gray-300">
                              {player.score || 'N/A'} / {player.quota || 'N/A'}
                            </td>
                            <td className="p-4 text-center dark:text-gray-300">{player.skins}</td>
                            <td className="p-4 text-center dark:text-gray-300">{player.ctps}</td>
                            <td className="p-4 text-center">
                              <span className={`text-lg font-bold ${player.correctFantasyPoints >= 10 ? 'text-green-600 dark:text-green-400' :
                                player.correctFantasyPoints < 0 ? 'text-red-600 dark:text-red-400' :
                                  'text-blue-600 dark:text-blue-400'
                                }`}>
                                {player.correctFantasyPoints}
                              </span>
                            </td>
                            <td className="p-4 text-left dark:text-gray-300">{player.picked_by || 'None'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {!loading && selectedEvent && (!playerBreakdown || playerBreakdown.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No player data available for this event.</p>
                </div>
              )}

              {!selectedEvent && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Select an event to view player performance breakdown.</p>
                </div>
              )}
            </div>
          )}

          {/* Pot Information */}
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700 p-6 mt-6">
            <h3 className="text-lg font-semibold text-forest-900 dark:text-white mb-4">Prize Pool Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-forest-50 dark:bg-navy-700 rounded-lg p-4">
                <span className="text-gray-600 dark:text-gray-400 font-medium">Total Pot</span>
                <div className="text-2xl font-bold text-forest-700 dark:text-forest-400">${standings.length * 20}</div>
              </div>
              <div className="bg-gold-50 dark:bg-gold-900/20 rounded-lg p-4">
                <span className="text-gray-600 dark:text-gray-400 font-medium">1st Place (50%)</span>
                <div className="text-xl font-bold text-gold-600 dark:text-gold-400">${standings.length * 10}</div>
              </div>
              <div className="bg-gray-100 dark:bg-navy-700 rounded-lg p-4">
                <span className="text-gray-600 dark:text-gray-400 font-medium">2nd Place (30%)</span>
                <div className="text-xl font-bold text-gray-600 dark:text-gray-300">${standings.length * 6}</div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <span className="text-gray-600 dark:text-gray-400 font-medium">3rd Place (20%)</span>
                <div className="text-xl font-bold text-amber-600 dark:text-amber-400">${standings.length * 4}</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
};

export default FantasyLeaderboard;
